import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '#/common/components/Modal';
import TMMButton from '#/common/components/TMMButton';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import { styled } from '@mui/system';
import FAIcon from '#/common/components/FAIcon';
import { color } from '@nuvolo/nuux-theme/utils';
import useOcrProcessing, { OcrFieldResult } from '#/common/hooks/useOcrProcessing';
import If from '#/common/components/If';
import StandardThrobber from '#/app/components/Throbbers/StandardThrobber';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';

const CardContainer = styled('div')<{ isDesktop?: boolean }>(({ isDesktop }) => ({
  display: `${isDesktop ? 'flex' : 'grid'}`,
  gap: '20px',
  justifyContent: 'center',
}));

const OptionCard = styled('div')<{ selected?: boolean }>(({ selected }) => ({
  width: '250px',
  height: '100px',
  border: `2px solid ${selected ? color('primary', 'dark') : color('primary', 'light3')}`,
  borderRadius: '12px',
  padding: '10px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  backgroundColor: `${selected ? color('custom', 'contrastText') : 'transparent'}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: `${selected ? color('primary', 'dark') : color('primary', 'light3')}`,
    backgroundColor: `${selected ? color('custom', 'contrastText') : color('primary', 'light3')}`,
  },
}));

const IconWrapper = styled('div')<{ selected?: boolean }>(({ selected }) => ({
  marginBottom: '5px',
  color: selected ? color('custom', 'invertedBackground') : color('custom', 'contrastText'),
}));

const CardTitle = styled(NuvoText)<{ selected?: boolean }>(({ selected }) => ({
  textAlign: 'center',
  fontWeight: '500',
  width: '50%',
  color: selected ? color('custom', 'invertedBackground') : color('custom', 'contrastText'),
}));

export interface ScanSelectorProps {
  onOcrResult: (file: File | null, result: OcrFieldResult, error?: string) => void;
  onClose: () => void;
  modalChoiceText?: string;
  fieldsToScan: string[];
}

type ScanOption = 'scan' | 'new' | null;

const ScanSelector: React.FC<ScanSelectorProps> = (props) => {
  const { onOcrResult, onClose, modalChoiceText, fieldsToScan } = props;

  const [selectedOption, setSelectedOption] = useState<ScanOption>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { isDesktop } = useViewport();

  // OCR processing hook
  const { processImageFile, loading: ocrLoading } = useOcrProcessing();

  const selectImage = useCallback(
    async (file: File) => {
      try {
        // Process image with OCR
        const ocrResults = await processImageFile(file, fieldsToScan);

        if (ocrResults.result) {
          // Call the callback with extracted data
          onOcrResult(file, ocrResults.result);
        } else if (ocrResults.error) {
          onOcrResult(null, {}, ocrResults.error);
        }
      } catch (error) {
        onOcrResult(null, {}, error instanceof Error ? error.message : 'Unknown error occurred');
      }
    },
    [processImageFile, onOcrResult],
  );

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        await selectImage(file);
        onClose();
      }
    },
    [selectImage, onClose],
  );

  const openFileSelector = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleNext = useCallback(() => {
    if (selectedOption === 'scan') {
      openFileSelector();
    } else if (selectedOption === 'new') {
      onClose();
    }
  }, [selectedOption, openFileSelector, onClose]);

  const cancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      <If condition={ocrLoading}>
        <StandardThrobber />
      </If>
      <If condition={!ocrLoading}>
        <Modal
          visible={!ocrLoading}
          title="How do you want to start?"
          dragEnabled={true}
          width={550}
          onHiding={cancel}
          content={
            <CardContainer isDesktop={isDesktop}>
              <OptionCard
                selected={selectedOption === 'scan'}
                onClick={() => setSelectedOption('scan')}>
                <IconWrapper selected={selectedOption === 'scan'}>
                  <FAIcon icon="barcode-read" />
                </IconWrapper>
                <CardTitle selected={selectedOption === 'scan'} type="body">
                  {modalChoiceText || 'Scan Image'}
                </CardTitle>
              </OptionCard>

              <OptionCard
                selected={selectedOption === 'new'}
                onClick={() => setSelectedOption('new')}>
                <IconWrapper selected={selectedOption === 'new'}>
                  <FAIcon icon="pen" />
                </IconWrapper>
                <CardTitle type="body" selected={selectedOption === 'new'}>
                  Start from Scratch
                </CardTitle>
              </OptionCard>
            </CardContainer>
          }
          actions={[
            <TMMButton key="cancel" label="Cancel" type="normal" onClick={cancel} />,
            <TMMButton
              key="ok"
              label="Next"
              type="submit"
              disabled={!selectedOption}
              onClick={handleNext}
            />,
          ]}
        />
      </If>
    </>
  );
};

export default React.memo(ScanSelector);
